/**
 * MultiSelectDropdown
 *
 * Popover + checkbox list. Empty `selected` Set = "All".
 * Mirrors the original Products filter pattern from PriceCatalogue.jsx.
 */

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ChevronDown } from 'lucide-react'

export function MultiSelectDropdown({
  options,
  selected,
  onChange,
  allLabel = 'All',
  width = 'w-48',
  align = 'start',
  disabled = false,
  emptyLabel = 'None available',
}) {
  const triggerText = (() => {
    if (selected.size === 0) return allLabel
    if (selected.size === 1) {
      const onlyId = [...selected][0]
      return options.find(o => o.id === onlyId)?.label || '1 selected'
    }
    return `${selected.size} selected`
  })()

  const toggle = (id) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`${width} justify-between font-normal`}
          disabled={disabled}
        >
          <span className="truncate">{triggerText}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={`${width} p-2`} align={align}>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent text-left"
            onClick={() => onChange(new Set())}
          >
            <Checkbox checked={selected.size === 0} />
            {allLabel}
          </button>
          {options.length > 0 && <div className="my-1 h-px bg-border" />}
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyLabel}</div>
          ) : (
            options.map(opt => (
              <button
                key={opt.id}
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent text-left"
                onClick={() => toggle(opt.id)}
              >
                <Checkbox checked={selected.has(opt.id)} />
                <span className="truncate">{opt.label}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default MultiSelectDropdown
